import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import AuthCallback from "@/components/AuthCallback";
import AuthPage from "@/pages/AuthPage";
import ChoosePathPage from "@/pages/ChoosePathPage";
import DashboardPage from "@/pages/DashboardPage";
import NewProjectPage from "@/pages/NewProjectPage";
import ResultsPage from "@/pages/ResultsPage";
import WhatIfPage from "@/pages/WhatIfPage";
import HistoryPage from "@/pages/HistoryPage";
import AdminSetupPage from "@/pages/AdminSetupPage";
import EmployeesPage from "@/pages/EmployeesPage";
import InvitePage from "@/pages/InvitePage";
import PulseCheckPage from "@/pages/PulseCheckPage";
import PricingPage from "@/pages/PricingPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import TrendPage from "@/pages/TrendPage";
import CampaignsPage from "@/pages/CampaignsPage";
import TradeOffPage from "@/pages/TradeOffPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
// ─── Marketing / Parent-Brand Pages ─────────────────────────────
import HomePage from "@/pages/marketing/HomePage";
import ProductsListingPage from "@/pages/marketing/ProductsListingPage";
import SynqProjectsPage from "@/pages/marketing/SynqProjectsPage";
import SynqEvolvePage from "@/pages/marketing/SynqEvolvePage";
import AboutPage from "@/pages/marketing/AboutPage";
import ContactPage from "@/pages/marketing/ContactPage";
import SolutionsPage from "@/pages/marketing/SolutionsPage";
import ResourcesPage from "@/pages/marketing/ResourcesPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-slate-500 font-ibm-plex">Loading SYNQ...</span>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  // Force path choice if not yet chosen (unless super admin)
  if (!user.account_type && !user.is_super_admin) return <Navigate to="/choose" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (!user.account_type && !user.is_super_admin) return <Navigate to="/choose" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function ChooseRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.account_type || user.is_super_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id from Google OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Marketing / Public site */}
      <Route path="/" element={<HomePage />} />
      <Route path="/products" element={<ProductsListingPage />} />
      <Route path="/products/projects" element={<SynqProjectsPage />} />
      <Route path="/products/evolve" element={<SynqEvolvePage />} />
      <Route path="/solutions" element={<SolutionsPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />

      {/* Auth + Onboarding */}
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/choose" element={<ChooseRoute><ChoosePathPage /></ChooseRoute>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />

      {/* SYNQ Projects app (protected) */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/project/new" element={<ProtectedRoute><NewProjectPage /></ProtectedRoute>} />
      <Route path="/project/:id" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
      <Route path="/simulate" element={<ProtectedRoute><WhatIfPage /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
      <Route path="/pulse" element={<ProtectedRoute><PulseCheckPage /></ProtectedRoute>} />
      <Route path="/trends" element={<ProtectedRoute><TrendPage /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
      <Route path="/tradeoffs" element={<ProtectedRoute><TradeOffPage /></ProtectedRoute>} />
      <Route path="/admin/setup" element={<ProtectedRoute><AdminSetupPage /></ProtectedRoute>} />
      <Route path="/admin/employees" element={<ProtectedRoute><EmployeesPage /></ProtectedRoute>} />
      <Route path="/admin/super" element={<ProtectedRoute><SuperAdminPage /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
